import { APITagAttributes, ArdoqId } from '@ardoq/api-types';
import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  persistentReducedStream,
  reducedStream,
  reducer,
  routine,
  spreadFilter,
  spreadMap,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { filter, map, pairwise, tap, withLatestFrom } from 'rxjs/operators';
import { difference } from 'lodash';
import { websocket$ } from 'sync/websocket$';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import { logWarn } from '@ardoq/logging';
import { applyTagAttributes } from './TagActions';
import { context$, workspacesClosed$ } from 'streams/context/context$';
import {
  getStreamCollectionInitialState,
  toStreamCollection,
} from 'streams/utils/streamUtils';
import { websocketReducer } from 'streams/crud/reducers';
import * as tagActions from './actions';
import {
  ardoqEventOperations,
  shouldProcessEventForBranch,
} from 'sync/ardoqEventOperations';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { isNotHandledByViewpointModeRoutines } from 'loadedState/isHandledByViewpointModeRoutine';

export const addTagsToState = (
  state: APITagAttributes[],
  tags: APITagAttributes[]
): APITagAttributes[] => [...state, ...tags];

export const removeTagsFromState = (
  state: APITagAttributes[],
  ids: ArdoqId[]
): APITagAttributes[] => {
  const idSet = new Set(ids);
  return state.filter(tag => !idSet.has(tag._id));
};

export const removeWorkspaceTagsFromState = (
  state: APITagAttributes[],
  workspaceId: ArdoqId
): APITagAttributes[] => state.filter(tag => tag.rootWorkspace !== workspaceId);

export const upsertTagInState = (
  state: APITagAttributes[],
  data: APITagAttributes
): APITagAttributes[] => {
  const exists = state.some(tag => tag._id === data._id);
  if (!exists) return [...state, data];
  return state.map(tag => (tag._id === data._id ? data : tag));
};

const handleAddTags = (
  state: APITagAttributes[],
  { tags }: { tags: APITagAttributes[] }
): APITagAttributes[] => {
  const existingIds = new Set(state.map(t => t._id));
  return addTagsToState(
    state,
    tags.filter(t => !existingIds.has(t._id))
  );
};

const handleResetTags = (): APITagAttributes[] => [];

const handleResetTagsWithData = (
  _: APITagAttributes[],
  { tags }: { tags: APITagAttributes[] }
): APITagAttributes[] => tags;

const handleFetchTagsSucceeded = (
  _: APITagAttributes[],
  tags: APITagAttributes[]
): APITagAttributes[] => tags;

const handleRemoveWorkspaceTags = (
  state: APITagAttributes[],
  { workspaceId }: { workspaceId: ArdoqId }
): APITagAttributes[] => removeWorkspaceTagsFromState(state, workspaceId);

const handleUpsertTags = (
  state: APITagAttributes[],
  tags: APITagAttributes[]
): APITagAttributes[] =>
  tags.reduce((s, data) => upsertTagInState(s, data), state);

const handleApplyTagAttributes = (
  state: APITagAttributes[],
  { _id, ...attributes }: { _id: ArdoqId } & Partial<APITagAttributes>
): APITagAttributes[] => {
  const existing = state.find(t => t._id === _id);
  if (!existing) {
    logWarn(Error('attempted to apply attributes to a nonexistent tag'), null, {
      _id,
    });
    return state;
  }
  return state.map(t => (t._id === _id ? { ...t, ...attributes } : t));
};

const handleWorkspacesClosed = (
  state: APITagAttributes[],
  { closed }: { closed: ArdoqId[] }
): APITagAttributes[] =>
  closed.reduce(
    (s, workspaceId) => removeWorkspaceTagsFromState(s, workspaceId),
    state
  );

const tagWebsocketEvents$ = websocket$.pipe(
  withLatestFrom(isViewpointMode$),
  spreadFilter(isNotHandledByViewpointModeRoutines),
  filter(ardoqEventOperations.isTagEvent),
  withLatestFrom(activeBranchId$),
  spreadFilter(shouldProcessEventForBranch)
);

const tagsRaw$ = reducedStream<APITagAttributes[]>(
  'tags$',
  [],
  [
    reducer(tagActions.addTags, handleAddTags),
    reducer(tagActions.resetTags, handleResetTags),
    reducer(tagActions.resetTagsWithData, handleResetTagsWithData),
    reducer(tagActions.removeWorkspaceTags, handleRemoveWorkspaceTags),
    reducer(tagActions.notifyFetchingTagsSucceeded, handleFetchTagsSucceeded),
    reducer(tagActions.upsertTags, handleUpsertTags),
    reducer(applyTagAttributes, handleApplyTagAttributes),
    streamReducer(
      tagWebsocketEvents$,
      websocketReducer({
        create: upsertTagInState,
        update: upsertTagInState,
        delete: (state, id) => removeTagsFromState(state, [id]),
      })
    ),
    streamReducer(workspacesClosed$, handleWorkspacesClosed),
  ]
);

export const tags$ = toPersistentStream(
  'tags$',
  tagsRaw$.pipe(map(toStreamCollection)),
  getStreamCollectionInitialState()
);

type FetchingTagsState = {
  fetchingStatus: 'idle' | 'loading' | 'success' | 'error';
};

const handleTagsIsFetching = (_: FetchingTagsState): FetchingTagsState => ({
  fetchingStatus: 'loading',
});

const handleTagsFetchSuccess = (_: FetchingTagsState): FetchingTagsState => ({
  fetchingStatus: 'success',
});

const handleTagsFetchError = (_: FetchingTagsState): FetchingTagsState => ({
  fetchingStatus: 'error',
});

const collectChangedEntityIds = (
  prev: APITagAttributes[],
  curr: APITagAttributes[]
): ArdoqId[] => {
  const prevById = new Map(prev.map(t => [t._id, t]));
  const ids: ArdoqId[] = [];
  for (const tag of curr) {
    const prevTag = prevById.get(tag._id);
    if (!prevTag) continue;
    ids.push(
      ...difference(tag.components ?? [], prevTag.components ?? []),
      ...difference(prevTag.components ?? [], tag.components ?? []),
      ...difference(tag.references ?? [], prevTag.references ?? []),
      ...difference(prevTag.references ?? [], tag.references ?? [])
    );
  }
  return ids;
};

export const tagEntityChanges$ = tagsRaw$.pipe(
  pairwise(),
  spreadMap(collectChangedEntityIds),
  filter(ids => ids.length > 0)
);

export const tagLoadingStatus$ = persistentReducedStream(
  'tagLoadingStatus$',
  { fetchingStatus: 'idle' },
  [
    reducer(tagActions.fetchTags, handleTagsIsFetching),
    reducer(tagActions.notifyFetchingTagsSucceeded, handleTagsFetchSuccess),
    reducer(tagActions.notifyFetchingTagsFailed, handleTagsFetchError),
  ]
);

const handleLoadTags = routine(
  ofType(tagActions.loadTags),
  extractPayload(),
  withLatestFrom(context$),
  tap(([tags, { workspaceIds }]) => {
    const loadedWorkspaces = new Set(workspaceIds);
    const tagsToLoad = tags.filter(t => loadedWorkspaces.has(t.rootWorkspace));
    dispatchAction(tagActions.addTags({ tags: tagsToLoad }));
  })
);

export const tagsCollectionRoutines = collectRoutines(handleLoadTags);
